import { Grid } from '@mui/material';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { TRIP_BOOKING_TYPE_CODES, TRIP_TYPE_CODES } from '@/constants/trips';
import Calendar from 'components/v2/Calendar';
import Dropdown from 'components/v2/Dropdown';
import InputField, { SIZES } from 'components/v2/InputField';
import TimePicker from 'components/v2/TimePicker';
import { DATE_FORMAT } from 'utils/constants';
import { getTimezoneLocalISO } from 'utils/v2';
import { waitingTimeOptions } from 'utils/v2/waitingTimeOptions';

/**
 * TripDateTimeSelector
 *
 * A reusable, controlled component for rendering the date, time, and optional wait time fields
 * for a trip leg.
 *
 * Features:
 * - Dynamically shows/hides the "Wait Time" field based on trip type and booking type.
 * - Automatically labels the date and time fields depending on trip type and booking type.
 * - Handles timezone conversion for date display via `getTimezoneLocalISO`.
 * - Fully controlled: parent component passes values, validation, and onChange handlers.
 *
 * Props:
 * @param {boolean} isScheduledTrip - Whether the trip is a scheduled trip (affects wait time display and layout)
 * @param {string} tripType - Type of the trip (e.g., SCHEDULED, ANCILLARY_SERVICES)
 * @param {string} selectedBookingType - Booking type for this leg (affects labels and field visibility)
 * @param {string} timezone - The current timezone for date calculations
 * @param {string} dateValue - ISO string representing the current selected date
 * @param {string} timeValue - String representing the current selected time
 * @param {string} waitTimeValue - Number/string representing the selected wait time in seconds
 * @param {function} onDateChange - Callback invoked when the date changes; receives the new date
 * @param {function} onTimeChange - Callback invoked when the time changes; receives the new time
 * @param {function} onWaitTimeChange - Callback invoked when the wait time changes
 * @param {string} dateError - Error message for the date field
 * @param {boolean} dateValid - Validation state for the date field
 * @param {string} timeError - Error message for the time field
 * @param {boolean} timeValid - Validation state for the time field
 * @param {string} waitTimeError - Error message for the wait time field
 * @param {boolean} waitTimeValid - Validation state for the wait time field
 * @param {number} legIndex - Index of the leg in the itinerary (affects layout/disabled state)
 * @param {boolean} isLoading - Whether the component is in a loading state
 *
 * Returns:
 * A React fragment containing the following fields arranged in a grid layout:
 * 1. **Calendar** – for selecting the trip date, with dynamic label and validation.
 * 2. **TimePicker** – for selecting the trip time, with dynamic label and validation.
 * 3. **Dropdown (optional)** – for selecting wait time, shown only if the trip is scheduled
 *    and the booking type is not WILLCALL. Includes validation and error display.
 *
 * Each field is fully controlled by the parent component via the corresponding value and
 * onChange handler, making this component reusable in Trip Creation, Trip Edit, or
 * Duplication forms.
 */

const TripDateTimeSelector = ({
  isScheduledTrip,
  tripType,
  selectedBookingType,
  timezone,
  dateValue,
  timeValue,
  waitTimeValue,
  onDateChange,
  onTimeChange,
  onWaitTimeChange,
  dateError,
  dateValid,
  timeError,
  timeValid,
  waitTimeError,
  waitTimeValid,
  legIndex = 0,
  isLoading = false,
  disabled = false,
  shouldFocusDateField,
  setShouldFocusDateField,
}) => {
  const calendarRef = useRef(null);
  // Determine whether the Wait Time dropdown should be displayed
  const showWaitTime = useMemo(
    () =>
      isScheduledTrip &&
      selectedBookingType !== TRIP_BOOKING_TYPE_CODES.WILLCALL,
    [isScheduledTrip, selectedBookingType],
  );

  // Dynamically compute the label for the date field based on trip type & booking type
  const pickupDateLabel = useMemo(() => {
    if (
      (tripType === TRIP_TYPE_CODES.SCHEDULED &&
        selectedBookingType === TRIP_BOOKING_TYPE_CODES.ARRIVE_BY) ||
      tripType === TRIP_TYPE_CODES.ANCILLARY_SERVICES
    ) {
      return 'Appointment Date';
    }
    return 'Date';
  }, [tripType, selectedBookingType]);

  // Dynamically compute the label for the time field
  const pickupTimeLabel = useMemo(
    () =>
      selectedBookingType === TRIP_BOOKING_TYPE_CODES.WILLCALL
        ? 'Estimated Time'
        : 'Time',
    [selectedBookingType],
  );

  // Convert date to localized timezone using utility function
  const appointmentDate = useMemo(
    () =>
      getTimezoneLocalISO({
        isoDate: dateValue,
        timezone,
      }),
    [dateValue, timezone],
  );

  const DateRenderInput = useCallback(
    (params) => (
      <InputField
        {...params}
        required
        size={SIZES.MEDIUM}
        valid={dateValid}
        error={dateError}
      />
    ),
    [dateValid, dateError],
  );

  // Compute the minimum selectable date to prevent invalid past dates
  const minDate = useMemo(() => {
    if (
      [
        TRIP_TYPE_CODES.SCHEDULED,
        TRIP_TYPE_CODES.ANCILLARY_SERVICES,
        TRIP_TYPE_CODES.MILEAGE_REIMBURSEMENT,
        TRIP_TYPE_CODES.PUBLIC_TRANSPORTATION,
      ].includes(tripType)
    ) {
      return DateTime.now()
        .minus({
          days: DateTime.fromISO(appointmentDate).isValid ? 1 : 0,
        })
        .endOf('day');
    }
    return 0;
  }, [tripType, appointmentDate]);

  // Focus the calendar when shouldFocusDateField is true
  useEffect(() => {
    if (shouldFocusDateField && calendarRef.current) {
      if (!calendarRef.current.disabled) {
        calendarRef.current.focus();
      }
      setShouldFocusDateField(false);
    }
  }, [shouldFocusDateField, calendarRef, disabled]); // Needed to add disabled in the dependency as we need to focus the field after the disable field is set to false

  return (
    <>
      <Grid item xs={12} sm={isScheduledTrip ? 4 : 6}>
        <Calendar
          ref={calendarRef}
          inputFormat={DATE_FORMAT}
          label={pickupDateLabel}
          value={appointmentDate}
          onChange={onDateChange}
          minDate={minDate}
          disabled={
            disabled ||
            isLoading ||
            selectedBookingType === TRIP_BOOKING_TYPE_CODES.ONDEMAND ||
            legIndex !== 0
          }
          renderInput={DateRenderInput}
          key={legIndex}
        />
      </Grid>

      <Grid item xs={12} sm={isScheduledTrip ? 4 : 6}>
        <TimePicker
          label={pickupTimeLabel}
          value={timeValue}
          onChange={onTimeChange}
          disabled={
            disabled ||
            isLoading ||
            selectedBookingType === TRIP_BOOKING_TYPE_CODES.ONDEMAND ||
            selectedBookingType === TRIP_BOOKING_TYPE_CODES.WILLCALL
          }
          required
          valid={timeValid}
          error={timeError}
        />
      </Grid>

      {showWaitTime && (
        <Grid item xs={12} sm={4}>
          <Dropdown
            label="Wait Time"
            size="medium"
            value={waitTimeValue || ''}
            options={waitingTimeOptions}
            onChange={onWaitTimeChange}
            valid={waitTimeValid}
            error={waitTimeError}
            disabled={isLoading}
          />
        </Grid>
      )}
    </>
  );
};

TripDateTimeSelector.propTypes = {
  isScheduledTrip: PropTypes.bool,
  tripType: PropTypes.string,
  selectedBookingType: PropTypes.string,
  timezone: PropTypes.string,
  dateValue: PropTypes.string,
  timeValue: PropTypes.string,
  waitTimeValue: PropTypes.string,
  onDateChange: PropTypes.func.isRequired,
  onTimeChange: PropTypes.func.isRequired,
  onWaitTimeChange: PropTypes.func,
  dateError: PropTypes.string,
  dateValid: PropTypes.bool,
  timeError: PropTypes.string,
  timeValid: PropTypes.bool,
  waitTimeError: PropTypes.string,
  waitTimeValid: PropTypes.bool,
  legIndex: PropTypes.number,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  shouldFocusDateField: PropTypes.bool,
  setShouldFocusDateField: PropTypes.func,
};

export default memo(TripDateTimeSelector);
